import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  announcementService,
  homeService,
  carouselSlideService,
  marketplaceListingService,
  auctionService,
  reviewService,
  priceSubmissionService,
  Announcement,
  CarouselSlide,
  PriceSubmission
} from '../services/api';
import Carousel from '../components/Carousel';
import { CreditCard, ShoppingBag, LayoutDashboard, Gavel } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Card {
  id: number;
  name: string;
  cardNumber: string;
  type: string;
  rarity: string;
  setName: string;
  imageUrl?: string;
  imageBlobPath?: string;
  language?: string;
  price_submission_count?: number;
  average_price?: number;
  watch_count?: number;
  wts_count?: number;
  wtb_count?: number;
  total_listings?: number;
}

interface Listing {
  id: number;
  price: number;
  condition: string;
  quantity: number;
  card?: Card;
  imageUrls?: string;
  customCardName?: string;
  bundleName?: string;
  isBundle?: boolean;
  seller: {
    id: number;
    displayName: string;
    profilePicture?: string;
  };
  createdAt: string;
}


interface Batch {
  id: string; // batchId
  batchId: string;
  title: string;
  endTime: string;
  auctionCount: number;
  images: string[];
  seller: {
    id: number;
    displayName: string;
    profilePicture?: string;
  };
}

const HomePage = () => {
  const { t } = useTranslation();
  const [carouselSlides, setCarouselSlides] = useState<CarouselSlide[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [activeCards, setActiveCards] = useState<Card[]>([]);
  const [topWatchlisted, setTopWatchlisted] = useState<Card[]>([]);
  const [topMarketplace, setTopMarketplace] = useState<Card[]>([]);
  const [recentListings, setRecentListings] = useState<Listing[]>([]);
  const [latestPrices, setLatestPrices] = useState<PriceSubmission[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRatings, setUserRatings] = useState<Record<number, { averageRating: number; totalReviews: number }>>({});
  const topValueListing = recentListings.reduce<Listing | null>(
    (top, listing) => (!top || Number(listing.price) > Number(top.price) ? listing : top),
    null
  );
  const newestListing = recentListings.reduce<Listing | null>(
    (latest, listing) => (!latest || new Date(listing.createdAt) > new Date(latest.createdAt) ? listing : latest),
    null
  );


  useEffect(() => {
    fetchHomeData();
  }, []);

  useEffect(() => {
    const ids = new Set<number>();
    batches.forEach((b) => b.seller?.id && ids.add(b.seller.id));
    recentListings.forEach((l) => l.seller?.id && ids.add(l.seller.id));
    const toFetch = Array.from(ids).filter((id) => userRatings[id] === undefined);
    if (toFetch.length === 0) return;

    const fetchRatings = async () => {
      const entries = await Promise.all(
        toFetch.map(async (id) => {
          try {
            const res = await reviewService.getUserReviews(String(id), { page: 1, limit: 1 });
            const summary = res.data.data.summary;
            return [id, { averageRating: summary?.averageRating || 0, totalReviews: summary?.totalReviews || 0 }] as const;
          } catch (err) {
            console.error('Failed to fetch rating for user', id, err);
            return [id, { averageRating: 0, totalReviews: 0 }] as const;
          }
        })
      );
      setUserRatings((prev) => {
        const next = { ...prev };
        entries.forEach(([id, rating]) => {
          next[id] = rating;
        });
        return next;
      });
    };

    fetchRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches, recentListings]);

  const fetchHomeData = async () => {
    try {
      const [carouselRes, announcementsRes, activeCardsRes, recentListingsRes, batchesRes, latestPricesRes, topWatchlistedRes, topMarketplaceRes] = await Promise.all([
        carouselSlideService.getCarouselSlides(),
        announcementService.getActiveAnnouncements(),
        homeService.getMostActiveCards(12),
        marketplaceListingService.getRecentListings(8),
        auctionService.getActiveBatches(6),
        priceSubmissionService.getLatestPriceUpdates(12),
        homeService.getTopWatchlisted(5),
        homeService.getTopMarketplaceActivity(5)
      ]);

      setCarouselSlides(carouselRes.data.data || []);
      setAnnouncements(announcementsRes.data.data || []);
      setActiveCards(activeCardsRes.data.data || []);
      setRecentListings(recentListingsRes.data.data || []);
      setBatches(batchesRes.data.data || []);
      setLatestPrices(latestPricesRes.data.data || []);
      setTopWatchlisted(topWatchlistedRes.data.data || []);
      setTopMarketplace(topMarketplaceRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch homepage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAnnouncementStyle = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-800';
      case 'warning':
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200 text-yellow-800';
      case 'error':
        return 'bg-gradient-to-r from-red-50 to-rose-50 border-red-200 text-red-800';
      default:
        return 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-800';
    }
  };

  const getAnnouncementIcon = (type: string) => {
    switch (type) {
      case 'success':
        return '✓';
      case 'warning':
        return '⚠';
      case 'error':
        return '✕';
      default:
        return 'ℹ';
    }
  };

  const getCountdown = (end: string) => {
    try {
      const diff = new Date(end).getTime() - Date.now();
      if (diff <= 0) return t('home.endingNow');
      const minutes = Math.floor(diff / (1000 * 60)) % 60;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours > 72) return `${Math.floor(hours / 24)}d ${hours % 24}h ${t('home.timeLeft')}`;
      if (hours > 0) return `${hours}h ${minutes}m ${t('home.timeLeft')}`;
      return `${minutes}m ${t('home.timeLeft')}`;
    } catch {
      return '';
    }
  };

  const getListingTitle = (listing: Listing) =>
    listing.card?.name || listing.customCardName || listing.bundleName || 'Listing';

  const getListingSubtitle = (listing: Listing) =>
    listing.card?.cardNumber || listing.customCardName || (listing.isBundle ? 'Bundle' : '');

  const getListingImage = (listing: Listing) => {
    let sellerImages: string[] = [];
    if (listing.imageUrls) {
      try {
        const parsed = JSON.parse(listing.imageUrls);
        sellerImages = Array.isArray(parsed) ? parsed : [];
      } catch {
        sellerImages = [];
      }
    }

    if (listing.isBundle && sellerImages.length > 0) {
      return sellerImages[0];
    }

    if (listing.card?.imageUrl || listing.card?.imageBlobPath) {
      return listing.card.imageUrl || listing.card.imageBlobPath;
    }

    return sellerImages[0];
  };

  const getRarityColor = (rarity: string) => {
    const rarityLower = rarity.toLowerCase();
    if (rarityLower === 'c' || rarityLower === 'common') return 'bg-gray-500 text-white';
    if (rarityLower === 'r' || rarityLower === 'rare') return 'bg-blue-500 text-white';
    if (rarityLower === 'sr' || rarityLower === 'super rare') return 'bg-purple-500 text-white';
    if (rarityLower === 'ur' || rarityLower === 'ultra rare') return 'bg-yellow-500 text-white';
    if (rarityLower === 'scr' || rarityLower === 'secret rare') {
      return 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 text-white';
    }
    if (rarityLower === 'cbr') return 'bg-[#D2B48C] text-black';
    if (rarityLower === 'pr') return 'bg-[#F5F5F5] text-black border border-gray-300';
    return 'bg-gray-500 text-white';
  };

  const getRarityAbbreviation = (rarity: string) => {
    const rarityLower = rarity.toLowerCase();
    if (rarityLower === 'c' || rarityLower === 'common') return 'C';
    if (rarityLower === 'r' || rarityLower === 'rare') return 'R';
    if (rarityLower === 'sr' || rarityLower === 'super rare') return 'SR';
    if (rarityLower === 'ur' || rarityLower === 'ultra rare') return 'UR';
    if (rarityLower === 'scr' || rarityLower === 'secret rare') return 'SCR';
    if (rarityLower === 'cbr') return 'CBR';
    if (rarityLower === 'pr') return 'PR';
    return rarity.toUpperCase();
  };

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= rating ? 'text-amber-500' : 'text-gray-300'}>
          ★
        </span>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Carousel Section */}
      {carouselSlides.length > 0 && <Carousel slides={carouselSlides} />}

      {/* Announcements Banner */}
      {announcements.length > 0 && (
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="container mx-auto px-4 py-4 max-w-7xl">
            <div className="space-y-3">
              {announcements.map((announcement) => (
                <div
                  key={announcement.id}
                  className={`${getAnnouncementStyle(announcement.type)} border rounded-lg p-4 shadow-sm`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0 mt-0.5">
                      {getAnnouncementIcon(announcement.type)}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-bold text-base mb-1">{announcement.title}</h3>
                      <p className="text-sm opacity-90">{announcement.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Feature Cards Section */}
      <div className="bg-gray-50 pt-8 pb-4">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {/* Browse Cards Feature */}
            <Link to="/browse-cards" className="bg-white rounded-xl p-4 md:p-8 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 block h-full">
              <div className="flex flex-col items-center text-center h-full">
                <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-3 md:mb-4 shadow-md">
                  <CreditCard className="w-5 h-5 md:w-8 md:h-8 text-white" />
                </div>
                <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-1 md:mb-2">{t('home.browseCardsTitle')}</h3>
                <p className="hidden md:block text-sm md:text-base text-gray-600 leading-relaxed">
                  {t('home.browseCardsDesc')}
                </p>
              </div>
            </Link>

            {/* Marketplace Feature */}
            <Link to="/marketplace" className="bg-white rounded-xl p-4 md:p-8 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 block h-full">
              <div className="flex flex-col items-center text-center h-full">
                <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-3 md:mb-4 shadow-md">
                  <ShoppingBag className="w-5 h-5 md:w-8 md:h-8 text-white" />
                </div>
                <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-1 md:mb-2">{t('home.marketplaceTitle')}</h3>
                <p className="hidden md:block text-sm md:text-base text-gray-600 leading-relaxed">
                  {t('home.marketplaceDesc')}
                </p>
              </div>
            </Link>

            {/* Auctions Feature */}
            <Link to="/auctions" className="bg-white rounded-xl p-4 md:p-8 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 block h-full">
              <div className="flex flex-col items-center text-center h-full">
                <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mb-3 md:mb-4 shadow-md">
                  <Gavel className="w-5 h-5 md:w-8 md:h-8 text-white" />
                </div>
                <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-1 md:mb-2">{t('nav.auctions')}</h3>
                <p className="hidden md:block text-sm md:text-base text-gray-600 leading-relaxed">
                  {t('home.bidNow')}
                </p>
              </div>
            </Link>

            {/* Your Dashboard Feature */}
            <Link to="/dashboard" className="bg-white rounded-xl p-4 md:p-8 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 block h-full">
              <div className="flex flex-col items-center text-center h-full">
                <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-3 md:mb-4 shadow-md">
                  <LayoutDashboard className="w-5 h-5 md:w-8 md:h-8 text-white" />
                </div>
                <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-1 md:mb-2">{t('home.dashboardTitle')}</h3>
                <p className="hidden md:block text-sm md:text-base text-gray-600 leading-relaxed">
                  {t('home.dashboardDesc')}
                </p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Batches Spotlight */}
      {batches.length > 0 && (
        <div className="bg-gradient-to-r from-indigo-50 via-white to-emerald-50 border-t border-gray-100">
          <div className="container mx-auto px-4 max-w-7xl py-14 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-indigo-600 uppercase tracking-[0.25em]">{t('home.liveAuctions')}</p>
                <h2 className="text-3xl font-bold text-gray-900 mt-2">{t('home.bidNow')}</h2>
                <p className="text-gray-600 mt-2">{t('home.endingSoon')}</p>
              </div>
              <div className="flex gap-3">
                <Link
                  to="/auctions/new"
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold shadow-md hover:bg-indigo-700"
                >
                  {t('home.createAuction')}
                </Link>
                <Link
                  to="/auctions"
                  className="px-4 py-2 rounded-lg border border-indigo-200 text-indigo-700 font-semibold hover:bg-indigo-50"
                >
                  {t('home.viewAll')}
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {batches.map((batch) => (
                <Link
                  to={`/auctions/batch/${batch.batchId}`}
                  key={batch.batchId}
                  className="group bg-white rounded-xl md:rounded-3xl border border-gray-100 shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col"
                >
                  {/* Image Grid */}
                  <div className="aspect-[4/3] bg-gray-100 relative p-0.5 md:p-1 grid grid-cols-2 gap-0.5 md:gap-1">
                    {batch.images.slice(0, 4).map((img, i) => (
                      <div key={i} className="relative overflow-hidden rounded-sm md:rounded bg-gray-200">
                        <img
                          src={img}
                          alt="Batch item"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ))}
                    {batch.images.length === 0 && (
                      <div className="col-span-2 row-span-2 flex items-center justify-center text-4xl text-gray-400">🎴</div>
                    )}

                    <div className="absolute bottom-2 left-2 md:bottom-3 md:left-3 px-2 py-1 md:px-3 md:py-1.5 rounded-full bg-indigo-600 text-white text-[10px] md:text-xs font-bold shadow z-10">
                      {getCountdown(batch.endTime)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-3 md:p-4 flex-1 flex flex-col">
                    <h3 className="text-sm md:text-lg font-bold text-gray-900 group-hover:text-indigo-700 transition-colors mb-0.5 md:mb-1 truncate">
                      {batch.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-500 mb-2 md:mb-4">{t('home.itemsInBatch', { count: batch.auctionCount })}</p>

                    <div className="mt-auto flex items-center justify-between border-t border-gray-100 pt-2 md:pt-3">
                      <div className="flex items-center gap-1.5 md:gap-2">
                        {batch.seller.profilePicture && (
                          <img src={batch.seller.profilePicture} alt={batch.seller.displayName} className="w-6 h-6 md:w-8 md:h-8 rounded-full border border-gray-200" />
                        )}
                        <div className="text-[10px] md:text-xs min-w-0">
                          <p className="text-gray-500 truncate hidden sm:block">{t('home.seller')}</p>
                          <p className="font-semibold text-gray-900 truncate max-w-[60px] sm:max-w-none">{batch.seller.displayName}</p>
                        </div>
                      </div>

                      {userRatings[batch.seller.id] && (
                        <div className="text-right hidden sm:block">
                          <div className="flex items-center gap-1 text-xs">
                            {renderStars(Math.round(userRatings[batch.seller.id].averageRating))}
                          </div>
                          <p className="text-[10px] text-gray-500">
                            {userRatings[batch.seller.id].totalReviews} reviews
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Marketplace Pulse */}
      {(activeCards.length > 0 || recentListings.length > 0) && (
        <div className="bg-gradient-to-b from-gray-50 via-white to-gray-50 pt-4 pb-16">
          <div className="container mx-auto px-4 max-w-7xl space-y-12">
            {/* Most Active Cards - Moved Up */}
            <div className="bg-white border border-gray-100 rounded-3xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-semibold text-emerald-600 uppercase tracking-[0.2em]">{t('home.activity')}</p>
                  <h3 className="text-2xl font-bold text-gray-900">{t('home.mostActiveCards')}</h3>
                </div>
                <Link
                  to="/browse-cards"
                  className="text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                >
                  {t('home.viewAll')}
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {activeCards.slice(0, 10).map((card) => (
                  <Link
                    key={card.id}
                    to={`/cards/${card.id}`}
                    className="group rounded-xl border border-gray-100 bg-white overflow-hidden shadow hover:shadow-lg transition-all duration-300"
                  >
                    <div className="aspect-[2.5/3.5] bg-gray-100 overflow-hidden">
                      {(card.imageUrl || card.imageBlobPath) ? (
                        <img
                          src={card.imageUrl || card.imageBlobPath}
                          alt={card.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                          {t('home.noImage')}
                        </div>
                      )}
                    </div>
                    <div className="p-3 space-y-1">
                      <h4 className="font-semibold text-sm text-gray-900 line-clamp-1 group-hover:text-emerald-700 transition-colors">
                        {card.name}
                      </h4>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-gray-500 font-medium bg-gray-100 px-1.5 py-0.5 rounded">
                          {card.cardNumber}
                        </span>
                        {card.rarity && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${getRarityColor(card.rarity)}`}>
                            {getRarityAbbreviation(card.rarity)}
                          </span>
                        )}
                        {card.language && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${card.language === 'EN' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                            {card.language}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                        {card.average_price ? (
                          <div className="flex items-center gap-1 text-emerald-600 font-semibold">
                            <span>฿{Number(card.average_price).toLocaleString()}</span>
                            <span className="text-gray-500 font-normal">avg.</span>
                          </div>
                        ) : (
                          <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-semibold">
                            {t('home.noPriceData')}
                          </span>
                        )}
                        {card.price_submission_count && (
                          <div className="flex items-center gap-1 text-emerald-600 font-semibold">
                            <span>🔥</span>
                            <span>{card.price_submission_count}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Latest Prices Section */}
            {latestPrices.length > 0 && (
              <div className="bg-white border border-gray-100 rounded-3xl shadow-xl p-6 mb-12">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-blue-600 uppercase tracking-[0.2em]">{t('home.latestPrices')}</p>
                    <h3 className="text-2xl font-bold text-gray-900">{t('home.recentlyReported')}</h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {latestPrices.map((submission) => (
                    <Link
                      key={submission.id}
                      to={`/cards/${submission.card.id}`}
                      className="group rounded-xl border border-gray-100 bg-white overflow-hidden shadow hover:shadow-lg transition-all duration-300"
                    >
                      <div className="aspect-[2.5/3.5] bg-gray-100 relative overflow-hidden">
                        {(submission.card.imageUrl || submission.card.imageBlobPath) ? (
                          <img
                            src={submission.card.imageUrl || submission.card.imageBlobPath}
                            alt={submission.card.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                            {t('home.noImage')}
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-8">
                          <p className="text-white font-bold text-lg">฿{Number(submission.submittedPrice).toLocaleString()}</p>
                          <p className="text-white/80 text-[10px] truncate">{new Date(submission.submittedAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="p-2">
                        <h4 className="font-semibold text-xs text-gray-900 line-clamp-1 group-hover:text-blue-600 transition-colors">
                          {submission.card.name}
                        </h4>
                        <div className="flex items-center gap-1.5 mt-0.5 mb-1">
                          <span className="text-[10px] text-gray-500 font-medium bg-gray-100 px-1.5 py-0.5 rounded">
                            {submission.card.cardNumber}
                          </span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${getRarityColor(submission.card.rarity || '')}`}>
                            {getRarityAbbreviation(submission.card.rarity || '')}
                          </span>
                          {submission.card.language && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${submission.card.language === 'EN' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                              {submission.card.language}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5 truncate">
                          By {submission.submitter?.displayName || t('common.anonymous')}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Listings */}
            {recentListings.length > 0 && (
              <div className="bg-white border border-gray-100 rounded-3xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-emerald-600 uppercase tracking-[0.2em]">{t('home.freshListings')}</p>
                    <h3 className="text-2xl font-bold text-gray-900">{t('home.hitMarketRecently')}</h3>
                  </div>
                  <Link
                    to="/marketplace"
                    className="hidden md:inline-flex px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-semibold shadow-lg shadow-emerald-200"
                  >
                    {t('home.viewMarketplace')}
                  </Link>
                </div>
                <div className="grid md:grid-cols-3 gap-4 justify-items-center">
                  {recentListings.slice(0, 6).map((listing) => (
                    <Link
                      key={listing.id}
                      to={`/marketplace`}
                      className="rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 overflow-hidden shadow hover:shadow-2xl transition-all duration-300 max-w-[250px] w-full"
                    >
                      <div className="aspect-[2.5/3.5] bg-gray-100 overflow-hidden relative">
                        {getListingImage(listing) ? (
                          <img
                            src={getListingImage(listing)}
                            alt={getListingTitle(listing)}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                            {t('home.noImage')}
                          </div>
                        )}
                        <div className="absolute top-3 right-3 bg-white/90 text-gray-900 px-3 py-1.5 rounded-full text-sm font-semibold shadow">
                          ฿{Number(listing.price).toLocaleString()}
                        </div>
                      </div>
                      <div className="p-4 space-y-2">
                        <h4 className="font-semibold text-gray-900 line-clamp-1">{getListingTitle(listing)}</h4>
                        <div className="flex items-center gap-1.5 mt-0.5 mb-1">
                          <span className="text-[10px] text-gray-500 font-medium bg-gray-100 px-1.5 py-0.5 rounded">
                            {getListingSubtitle(listing)}
                          </span>
                          {listing.card?.rarity && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${getRarityColor(listing.card.rarity)}`}>
                              {getRarityAbbreviation(listing.card.rarity)}
                            </span>
                          )}
                          {listing.card?.language && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${listing.card.language === 'EN' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                              {listing.card.language}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {t('home.qty')} {listing.quantity}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {listing.seller.profilePicture && (
                            <img
                              src={listing.seller.profilePicture}
                              alt={listing.seller.displayName}
                              className="w-6 h-6 rounded-full"
                            />
                          )}
                          <div className="min-w-0">
                            <Link to={`/users/${listing.seller.id}/reviews`} className="truncate text-indigo-700 font-semibold hover:underline">
                              {listing.seller.displayName}
                            </Link>
                            {userRatings[listing.seller.id] && (
                              <div className="flex items-center gap-1 text-[11px] text-gray-600">
                                {renderStars(Math.round(userRatings[listing.seller.id].averageRating))}
                                <span className="font-semibold text-gray-800">
                                  {userRatings[listing.seller.id].averageRating.toFixed(1)}
                                </span>
                                <span className="text-gray-500">({userRatings[listing.seller.id].totalReviews})</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="mt-6 text-center md:hidden">
                  <Link
                    to="/marketplace"
                    className="inline-block px-6 py-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-semibold shadow-lg shadow-emerald-200"
                  >
                    {t('home.viewMarketplace')}
                  </Link>
                </div>
              </div>
            )}

            {/* Market Info - Moved to Bottom */}
            <div className="space-y-6 pt-12 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-emerald-600 uppercase tracking-[0.2em]">{t('home.marketPulse')}</p>
                  <h2 className="text-3xl font-bold text-gray-900 mt-2">{t('home.marketIntel')}</h2>
                </div>
                <Link
                  to="/marketplace"
                  className="hidden md:inline-flex px-4 py-2 rounded-lg border border-gray-200 text-gray-800 hover:bg-gray-100 font-semibold"
                >
                  {t('home.openMarketplace')}
                </Link>
              </div>
              {/* Top Lists Section */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Top Watchlisted */}
                <div className="bg-white border border-gray-100 rounded-2xl shadow-lg p-6 w-full max-w-full overflow-hidden">
                  <div className="flex items-center gap-2 mb-6">
                    <h3 className="text-xl font-bold text-gray-900">{t('home.topWatchlisted')}</h3>
                  </div>
                  <div className="space-y-4">
                    {topWatchlisted.map((card, index) => (
                      <Link
                        key={card.id}
                        to={`/cards/${card.id}`}
                        className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
                      >
                        <div className="flex-shrink-0 w-8 text-center font-bold text-gray-400">
                          #{index + 1}
                        </div>
                        <div className="flex-shrink-0 w-12 h-16 bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={card.imageUrl || card.imageBlobPath}
                            alt={card.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 truncate">{card.name}</h4>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{card.cardNumber}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${getRarityColor(card.rarity)}`}>
                              {getRarityAbbreviation(card.rarity)}
                            </span>
                            {card.language && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${card.language === 'EN' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                {card.language}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <span className="block font-bold text-indigo-600">{card.watch_count}</span>
                          <span className="text-xs text-gray-400">{t('home.watchers')}</span>
                        </div>
                      </Link>
                    ))}
                    {topWatchlisted.length === 0 && (
                      <p className="text-center text-gray-500 py-4">{t('home.noData')}</p>
                    )}
                  </div>
                </div>

                {/* Top Marketplace Activity */}
                <div className="bg-white border border-gray-100 rounded-2xl shadow-lg p-6 w-full max-w-full overflow-hidden">
                  <div className="flex items-center gap-2 mb-6">
                    <h3 className="text-xl font-bold text-gray-900">{t('home.topMarketplaceActivity')}</h3>
                  </div>
                  <div className="space-y-4">
                    {topMarketplace.map((card, index) => (
                      <Link
                        key={card.id}
                        to={`/cards/${card.id}`}
                        className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
                      >
                        <div className="flex-shrink-0 w-8 text-center font-bold text-gray-400">
                          #{index + 1}
                        </div>
                        <div className="flex-shrink-0 w-12 h-16 bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={card.imageUrl || card.imageBlobPath}
                            alt={card.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 truncate">{card.name}</h4>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{card.cardNumber}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${getRarityColor(card.rarity)}`}>
                              {getRarityAbbreviation(card.rarity)}
                            </span>
                            {card.language && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${card.language === 'EN' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                {card.language}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-3 text-xs">
                          <div className="text-center">
                            <span className="block font-bold text-emerald-600">{card.wts_count}</span>
                            <span className="text-gray-400">{t('home.selling')}</span>
                          </div>
                          <div className="w-px h-8 bg-gray-200"></div>
                          <div className="text-center">
                            <span className="block font-bold text-blue-600">{card.wtb_count}</span>
                            <span className="text-gray-400">{t('home.buying')}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                    {topMarketplace.length === 0 && (
                      <p className="text-center text-gray-500 py-4">{t('home.noData')}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="rounded-2xl bg-white shadow-lg border border-gray-100 p-6">
                  <p className="text-sm text-gray-500 mb-2">{t('home.mostValuable')}</p>
                  {topValueListing ? (
                    <>
                      <h3 className="text-xl font-semibold text-gray-900 mb-1 truncate">{getListingTitle(topValueListing)}</h3>
                      <p className="text-sm text-gray-500 mb-4">{getListingSubtitle(topValueListing)}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-3xl font-bold text-emerald-600">
                          ฿{Number(topValueListing.price).toLocaleString()}
                        </span>
                        <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                          {t(`marketplace.condition.${topValueListing.condition.toLowerCase().replace(' ', '_')}`, topValueListing.condition)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-500">Listings will appear as soon as they land.</p>
                  )}
                </div>
                <div className="rounded-2xl bg-white shadow-lg border border-gray-100 p-6">
                  <p className="text-sm text-gray-500 mb-2">{t('home.freshestDrop')}</p>
                  {newestListing ? (
                    <>
                      <h3 className="text-xl font-semibold text-gray-900 mb-1 truncate">{getListingTitle(newestListing)}</h3>
                      <p className="text-sm text-gray-500 mb-4">{new Date(newestListing.createdAt).toLocaleString()}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-semibold text-gray-900">
                          ฿{Number(newestListing.price).toLocaleString()}
                        </span>
                        <span className="text-sm text-gray-500">{t('home.qty')} {newestListing.quantity}</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-500">No new listings yet today.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
            <p className="mt-4 text-gray-600 text-lg">{t('home.loading')}</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && activeCards.length === 0 && recentListings.length === 0 && (
        <div className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <div className="text-8xl mb-6">🎴</div>
            <h3 className="text-3xl font-bold text-gray-900 mb-3">{t('home.welcomeTitle')}</h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              {t('home.welcomeDesc')}
            </p>
            <Link
              to="/browse-cards"
              className="inline-block px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all transform hover:scale-105 font-semibold text-lg shadow-lg"
            >
              {t('home.browseCardsButton')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
